import { useMutation } from "@tanstack/react-query";
import { getSupabaseClient } from "../providers/supabase/supabase";

export type RescanContactAction = {
  action: "rescan_contact";
  contact_id: number;
  company_id?: number | null;
  force_reenrich?: boolean;
};

export type NudgeTowardOfferAction = {
  action: "nudge_toward_offer";
  contact_id: number;
  target_door?: string | null;
  target_list_id?: number | null;
};

export type TriggerAgentActionPayload = RescanContactAction | NudgeTowardOfferAction;

type TriggerAgentActionResult = {
  ok: boolean;
  agent_run_id?: string;
  error?: string;
};

/**
 * Calls the trigger-agent-action Edge Function (deployed 2026-09-04, see
 * Martha AI BDR/docs/agent-crm-integration-decisions.md Q4). The function
 * itself holds Marco's/Martha's webhook secrets server-side and logs every
 * call to mentorax.agent_runs — this hook is a thin client, no secrets here.
 *
 * Marco's single-contact re-scan webhook and Martha's 7th Hermes route don't
 * exist yet, so a call here will succeed at logging the request (visible in
 * agent_runs) but report "not configured yet" until those two follow-ups
 * are built — that's expected, not a bug in this hook.
 */
export function useTriggerAgentAction() {
  return useMutation({
    mutationFn: async (
      payload: TriggerAgentActionPayload,
    ): Promise<TriggerAgentActionResult> => {
      const {
        data: { session },
      } = await getSupabaseClient().auth.getSession();
      if (!session) {
        throw new Error("Not signed in.");
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/trigger-agent-action`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(payload),
        },
      );

      const result = (await response.json()) as TriggerAgentActionResult;
      if (!response.ok) {
        throw new Error(result.error ?? `Request failed (${response.status})`);
      }
      return result;
    },
  });
}
